/**
 * @fileoverview Editable text-row list used by every lesson-authoring surface —
 * LessonSummary's edit mode and the manual-lesson compose page — plus the field
 * styling and list-item helpers they share.
 *
 * It lives here rather than inside LessonSummary because typing a lesson up and
 * editing one are the same document interaction: two copies would drift, and
 * the compose page exists precisely so the two look identical.
 */

import { Icon } from "@/components/studio/primitives";

/** Shared input/textarea styling for lesson edit fields (studio token layer). */
export const FIELD_CLS =
  "w-full rounded-lg border border-[var(--studio-divider)] bg-[var(--studio-base)] px-3 py-2 font-sans text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] focus:border-[var(--accent-yellow-border-strong)] focus:outline-none";

let listKeyCounter = 0;
// List items are carried as {id, value} rather than bare strings so React keys
// stay stable while a row is edited or removed — index keys get reused across a
// delete and strand focus/value on the wrong row.
export const toListItems = (arr) => (arr || []).map((value) => ({ id: ++listKeyCounter, value }));
export const fromListItems = (items) => items.map((i) => i.value);
export const blankItem = () => ({ id: ++listKeyCounter, value: "" });
// Blank rows are an artifact of the "Add item" affordance, not content — drop
// them on save so an abandoned empty row never lands in a parent's email.
export const cleanList = (items) => fromListItems(items).map((v) => v.trim()).filter(Boolean);

/**
 * Editable list of text items with per-row remove and an append affordance.
 * Used for practice items, key elements, student requests and core concepts.
 * @param {{items: Array<{id:number, value:string}>, onUpdate: Function,
 *   onRemove: Function, onAdd: Function, numbered?: boolean, compact?: boolean,
 *   placeholder?: string, addLabel?: string, testIdPrefix: string}} props
 */
export function EditableList({
  items, onUpdate, onRemove, onAdd, numbered, compact, placeholder, addLabel = "Add item", testIdPrefix,
}) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((item, i) => (
        <div key={item.id} className="flex items-start gap-2.5">
          {numbered ? (
            <span className="mt-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] font-mono text-[12px] font-semibold text-[var(--accent-yellow-text)]">
              {i + 1}
            </span>
          ) : (
            <span className="mt-2 shrink-0 font-sans text-[var(--text-tertiary)]">•</span>
          )}
          <input
            type="text"
            value={item.value}
            placeholder={placeholder}
            onChange={(e) => onUpdate(i, e.target.value)}
            className={`${FIELD_CLS} ${compact ? "text-[13px]" : "text-[15px]"}`}
            data-testid={`${testIdPrefix}-input-${i}`}
          />
          <button
            type="button"
            onClick={() => onRemove(i)}
            aria-label="Remove item"
            data-testid={`${testIdPrefix}-remove-${i}`}
            className="mt-1.5 shrink-0 rounded-full p-1 text-[var(--text-tertiary)] transition-colors hover:text-[var(--state-danger)]"
          >
            <Icon name="x" size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={onAdd}
        data-testid={`${testIdPrefix}-add`}
        className="mt-1 flex items-center gap-1.5 self-start rounded-full border border-[var(--studio-divider)] px-3 py-1 font-sans text-xs text-[var(--text-secondary)] transition-colors hover:border-[var(--accent-yellow-border)] hover:text-[var(--accent-yellow-text)]"
      >
        <Icon name="plus" size={13} /> {addLabel}
      </button>
    </div>
  );
}

/**
 * Bind an EditableList to a `useState` setter holding {id, value} rows.
 * @returns {Object} props to spread onto <EditableList/>.
 */
export function listProps(items, setter, testIdPrefix, extra = {}) {
  return {
    items,
    testIdPrefix,
    onUpdate: (i, v) => setter((prev) => prev.map((item, idx) => (idx === i ? { ...item, value: v } : item))),
    onRemove: (i) => setter((prev) => prev.filter((_, idx) => idx !== i)),
    onAdd: () => setter((prev) => [...prev, blankItem()]),
    ...extra,
  };
}
